import { onReady } from './utils/dom';
import { toArray } from './utils/js';
import EventManager from './EventManager';

const KEY_WHITELIST = ['Meta', 'Alt', 'Control', 'Shift'];

/**
 * Class to watch for currently pressed meta keys (Ctrl, Alt, Shift...).
 */
class ModifierKeyWatcher extends EventManager {
  /**
   * Currently held keys.
   */
  _keys = new Set();

  /**
   * Bind events on DOM ready.
   */
  constructor() {
    super();

    onReady(this._bindEvents);
  }

  /**
   * Bind keydown and keyup events to set and unset active keys.
   */
  _bindEvents = () => {
    const html = document.documentElement;
    this.on(html, 'keydown', this._handleKeydown);
    this.on(html, 'keyup', this._handleKeyup);
  };

  /**
   * Add pressed key to the set.
   *
   * @param {object} e - Keydown event.
   */
  _handleKeydown = (e) => {
    if (KEY_WHITELIST.includes(e.key)) {
      this._keys.add(e.key);
    }
  };

  /**
   * Remove pressed key from the set.
   *
   * @param {object} e - Keyup event.
   */
  _handleKeyup = (e) => {
    if (KEY_WHITELIST.includes(e.key)) {
      this._keys.delete(e.key);
    }
  };

  /**
   * Check if holding specific key(s).
   *
   * @param {string|Array.<string>} keys - Key(s) to check for.
   * @param {boolean} [all] - If all specified keys are held. Defaults to false,
   *   checking if any one of the keys are held.
   * @returns {boolean}
   */
  isHolding = (keys, all = false) => {
    const method = all ? 'every' : 'some';
    return toArray(keys)[method]((key) => this._keys.has(key));
  };

  /**
   * Check if holding any meta key.
   *
   * @returns {boolean}
   */
  isHoldingAny = () => Boolean(this._keys.size);

  /**
   * Check if holding a key to open links in new tabs.
   *
   * This is Control on Windows and Linux, Command on macOS (which is mapped
   * to the 'meta' key).
   *
   * @returns {boolean}
   */
  isHoldingNewTabKey = () => this.isHolding(['Meta', 'Control']);
}

const watcher = new ModifierKeyWatcher();

export default watcher;
